import { giphyIndigo, giphyLightCharcoal, giphyWhite } from '@giphy/colors'
import dateFormat from 'dateformat'
import { at, filter, range, uniqueId } from 'lodash'
import { useContext, useEffect, useState } from 'react'
import DotsLoader from 'shared/components/loaders/dots'
import MessageContext from 'shared/contexts/message'
import { trackPageView } from 'shared/util/analytics'
import CreationToolFont from 'site/creation-tools/types/font'
import styled, { css } from 'styled-components'
import { CreationToolsContext } from '../../../manager'
import CreationTool from '../../../types/creation-tool'
import CreationToolOutputType from '../../../types/output-type'
import { PillBlock } from '../../blocks'
import EditCaptureModal from '../modals/edit-capture'
import EditSegmentModal from '../modals/edit-segment'
import Footer from '../shared/components/footer'
import ProgressBar from '../shared/components/progress-bar'
import SupportCTA from '../shared/components/support-cta'
import SelectAll from '../shared/icons/select-all'
import UnselectAll from '../shared/icons/unselect-all'
import { ICapture, ISegment } from '../shared/types'
import API from '../shared/utils/api'
import { getMergedCaption } from '../shared/utils/caption'
import getSegmentCaptionWatermark from '../shared/utils/get-segment-caption-watermark'
import Imaging from '../shared/utils/imaging'
import Uploader from '../shared/utils/uploader'
import NoMatch from './404'
import UploadButtons from './buttons'
import Grid from './grid'
import Header from './header'

const api = new API()

const BottomBar = styled.div`
    left: 0;
    bottom: 0;
    width: 100%;
    margin: 50px 0px;
`

const TopBar = styled.div`
    padding: 30px 0;
    position: sticky;
    top: 0;
    background-color: black;
    z-index: 1;
`

const TabLabel = styled.div<{ active: boolean }>`
    cursor: pointer;
    color: ${giphyLightCharcoal};
    font-size: 17px;
    font-weight: bold;
    display: inline;
    margin-right: 20px;

    ${(props) =>
        props.active &&
        css`
            color: ${giphyWhite};
        `}
`

const Actions = styled.div`
    float: right;
    font-size: 14px;
    color: ${giphyLightCharcoal};
    font-weight: bold;
`

const Action = styled.span`
    margin: 0 10px;
    cursor: pointer;
`

const ActionIcon = styled.span`
    padding-right: 7px;
    vertical-align: middle;
    color: ${giphyWhite};
`

const Pill = styled(PillBlock)`
    background: ${giphyIndigo};
    margin-left: 5px;
`

const LoaderContainer = styled.div`
    background: #000;
    height: 100%;
    left: 0;
    position: fixed;
    top: 0;
    width: 100%;
    z-index: 100;
`

const Detail = ({ match }: any) => {
    const { tool, trackEvent, setFiles } = useContext(CreationToolsContext)
    const [capture, setCapture] = useState<ICapture>()
    const [captureMissing, setCaptureMissing] = useState<boolean>(false)
    const [originalSegments, setOriginalSegments] = useState<ISegment[]>([])
    const [editedSegments, setEditedSegments] = useState<ISegment[]>([])
    const [selected, setSelected] = useState<number[]>([])
    const [editing, setEditing] = useState<number>()
    const [activeTab, setActiveTab] = useState('all')
    const [segments, setSegments] = useState<ISegment[]>([])
    const [soundEnabled, setSoundEnabled] = useState(false)
    const { sendMessage } = useContext(MessageContext)
    const [showEditCaptureModal, setShowEditCaptureModal] = useState(false)
    const [isProcessingUpload, setIsProcessingUpload] = useState(false)
    const captureId = match.params.captureId
    const gaCategory = CreationTool.gaEventCategory(tool)
    const captureStatus = capture?.status
    const storageName = `${captureId}-selected`

    useEffect(() => {
        trackPageView(`/supercut/detail/${captureId}`)

        const fetchCaptureDetail = async () => {
            try {
                const data: { capture: ICapture; segments: ISegment[] } = await api.getCaptureDetails(captureId)
                setCapture(data.capture)
                setOriginalSegments(data.segments)

                // update selected segments after they load
                const cachedSelections = localStorage.getItem(storageName) || ''
                if (cachedSelections) {
                    setSelected(cachedSelections.split(',').map((i: string) => Number(i)))
                }
            } catch (e) {
                setCaptureMissing(true)
            }
        }

        const fetchUserEdits = async () => {
            const data = await api.getUserEdits(captureId)
            setEditedSegments(data)
        }

        fetchCaptureDetail()
        fetchUserEdits()
    }, [])

    useEffect(() => {
        refreshSegmentsData()
    }, [originalSegments, editedSegments, capture?.custom_font])

    /**
     * convenience function to update state and localStorage in one place
     */
    const selectSegmentHandler = (segments: number[]) => {
        setSelected(segments)
        if (segments.length === 0) {
            localStorage.removeItem(storageName)
        } else {
            localStorage.setItem(storageName, segments.join(','))
        }
    }

    const saveUserEdit = (segment: ISegment) => {
        let update = [...editedSegments]
        update[segment.index] = segment
        setEditedSegments(update)
        api.saveUserEdits(capture!.id, update)
    }

    const editClosed = () => {
        setEditing(undefined)
    }

    const soundClicked = () => {
        trackEvent(gaCategory, `Sound Toggle ${soundEnabled ? 'On' : 'Off'}`, captureId)
        setSoundEnabled(!soundEnabled)
    }

    const uploadClicked = async (isClips: boolean) => {
        const outputType = isClips ? CreationToolOutputType.Video : CreationToolOutputType.Gif

        trackEvent(gaCategory, `Continue to Upload ${isClips ? 'CLIP' : 'GIF'}`, captureId, String(selected.length))
        setIsProcessingUpload(true)

        const selectedURLs = await Uploader.getVideoURLs(
            selected.map((i) => segments[i]),
            capture
        )
        const sources = await Uploader.processURLs(selectedURLs, capture!)

        setIsProcessingUpload(false)

        setFiles(sources, outputType)
    }

    const selectAllClicked = () => {
        if (selected.length !== segments.length) {
            trackEvent(gaCategory, 'Select All', captureId)
        }
        selectSegmentHandler(range(segments.length))
    }

    const unselectAllClicked = () => {
        if (selected.length > 0) {
            trackEvent(gaCategory, 'Unselect All', captureId)
        }
        selectSegmentHandler([])
    }

    const settingsClicked = async () => {
        trackEvent(gaCategory, 'Edit Capture Click', captureId)
        setShowEditCaptureModal(true)
    }

    const closeEditModal = () => {
        setShowEditCaptureModal(false)
    }

    const getSegmentDefaults = async (segment: ISegment): Promise<ISegment> => {
        const editParams = segment.editParams || {
            displayHidden: false,
            displayOrder: 0,
            isMerged: false,
        }
        let captionPosition = capture?.upload_metadata?.caption_position
        const captionStyle = capture?.upload_metadata?.caption_style
        const captionWatermarkUrl = await getSegmentCaptionWatermark(segment, capture!)

        // in case of letterbox cropping from DVR, make sure the caption is in frame
        // TODO: probably a better way to do this than post-cut
        if (captionPosition && captionPosition.y >= capture!.height) {
            captionPosition.y = capture!.height - (captionPosition.y - capture!.height)
        }

        if (segment.caption && captionStyle && captionPosition && !segment.editParams?.captions) {
            return {
                ...segment,
                editParams: {
                    ...editParams,
                    watermark: captionWatermarkUrl,
                    captions: [
                        {
                            id: uniqueId('caption'),
                            text: segment.caption,
                            x: captionPosition.x,
                            y: captionPosition.y,
                            rotation: captionPosition.rotation,
                            style: captionStyle,
                            font: capture.custom_font
                                ? capture.custom_font?.fontUrl
                                : CreationToolFont.fontFamily(CreationToolFont.Giphy),
                            options: {},
                        },
                    ],
                },
            }
        }
        return segment
    }

    const refreshSegmentsData = async () => {
        let segments = [] as ISegment[]

        if (capture && originalSegments) {
            // Merge the original segments from this capture with any edited/custom segments.
            for (let i = 0; i < Math.max(originalSegments.length, editedSegments.length); i++) {
                if (editedSegments[i]) {
                    segments[i] = editedSegments[i]
                } else if (originalSegments[i]) {
                    segments[i] = await getSegmentDefaults(originalSegments[i])
                }
            }
        }

        setSegments(segments)
    }

    const getMergedSegment = (mergeSegments: ISegment[]): ISegment | undefined => {
        if (!capture || mergeSegments.length < 2) return

        const sortedIndexes = mergeSegments.map((s) => s.index).sort()
        const sortedSegments = sortedIndexes.map((i) => segments[i])
        const firstSegment = sortedSegments[0]
        const lastSegment = sortedSegments[sortedSegments.length - 1]

        if (!firstSegment || !lastSegment) return

        // This reverse() is in there because otherwise the gif gets merged backwards.
        // Easier to fix here than in imaging service.
        const mergedVideoUrl = Imaging.concatenate(sortedSegments.map((s) => s.video_url).reverse(), 'mp4')
        const mergedPreviewUrl = Imaging.concatenate(sortedSegments.map((s) => s.preview_url).reverse(), 'gif')
        const { mergedCaption, mergedCaptionLayers } = getMergedCaption(firstSegment, lastSegment)

        const mergedWidth = Math.max(...mergeSegments.map((o) => o.width))
        const mergedHeight = Math.max(...mergeSegments.map((o) => o.height))

        return {
            index: segments.length,
            start_time: firstSegment.start_time,
            end_time: lastSegment.end_time,
            duration: firstSegment.duration + lastSegment.duration,
            thumb_url: firstSegment.thumb_url,
            video_url: mergedVideoUrl,
            preview_url: mergedPreviewUrl,
            caption: mergedCaption,
            width: mergedWidth,
            height: mergedHeight,
            editParams: {
                displayOrder: lastSegment.index,
                displayHidden: false,
                isMerged: true,
                captions: mergedCaptionLayers,
            },
        }
    }

    const mergeClicked = () => {
        if (!capture || !selected.length) return

        const selectedSegments = selected.map((i) => segments[i])
        const segment = getMergedSegment(selectedSegments)
        if (segment) {
            saveUserEdit(segment)
            refreshSegmentsData()
            trackEvent(gaCategory, 'Merge', captureId)
            sendMessage('Merge Successful!')
        }
    }

    if (captureMissing) {
        return <NoMatch />
    }

    if (!capture) {
        return null
    } else {
        return (
            <div>
                {showEditCaptureModal && (
                    <EditCaptureModal
                        capture={capture}
                        updateCapture={setCapture}
                        preview={segments[0].video_url}
                        onClose={closeEditModal}
                    />
                )}
                {capture && (
                    <Header
                        title={capture!.title}
                        subtitle={dateFormat(new Date(capture!.date), 'mmm dS, yyyy h:MM TT')}
                    />
                )}

                {editing !== undefined && (
                    <EditSegmentModal
                        {...{
                            capture: capture!,
                            segment: segments[editing],
                            onClose: editClosed,
                            onSave: (segment: ISegment) => {
                                saveUserEdit(segment)
                                editClosed()
                            },
                        }}
                    />
                )}

                <TopBar>
                    <TabLabel onClick={() => setActiveTab('all')} active={activeTab === 'all'}>
                        All Scenes <Pill>{segments.length}</Pill>
                    </TabLabel>
                    <TabLabel onClick={() => setActiveTab('selected')} active={activeTab === 'selected'}>
                        Selected Scenes <Pill>{selected.length}</Pill>
                    </TabLabel>

                    <Actions>
                        <Action onClick={soundClicked}>
                            <ActionIcon className={soundEnabled ? 'ss-highvolume' : 'ss-volume'} />
                            Sound
                        </Action>
                        {selected.length > 1 && (
                            <Action onClick={mergeClicked}>
                                <ActionIcon className="ss-merge" />
                                Merge Clips
                            </Action>
                        )}
                        <Action onClick={selectAllClicked}>
                            <SelectAll /> Select All
                        </Action>
                        {selected.length > 0 && (
                            <Action onClick={unselectAllClicked}>
                                <UnselectAll /> Unselect All
                            </Action>
                        )}
                        <Action onClick={settingsClicked}>
                            <ActionIcon className="ss-settings" />
                            Edit Capture
                        </Action>
                    </Actions>
                </TopBar>

                {captureStatus === 'IN_PROGRESS' && <ProgressBar />}

                <Grid
                    capture={capture}
                    segments={activeTab === 'all' ? segments : filter(at(segments, selected), (s) => !!s)}
                    selectedIndexes={selected}
                    isSoundOn={soundEnabled}
                    isEditModalOpen={!!editing}
                    onSelect={selectSegmentHandler}
                    onEditClick={(index) => {
                        trackEvent(gaCategory, 'Edit Click', captureId)
                        setEditing(index)
                    }}
                    onDuplicateClick={(index) => {
                        const segment = segments[index]
                        saveUserEdit({
                            ...segment,
                            index: segments.length,
                            editParams: {
                                ...(segment.editParams || {}),
                                displayOrder: index,
                            },
                        } as ISegment)
                        trackEvent(gaCategory, 'Duplicate Cut', captureId)
                        sendMessage('Clip Duplicated!')
                    }}
                    onDeleteClick={(index) => {
                        const segment = segments[index]
                        saveUserEdit({
                            ...segment,
                            editParams: {
                                ...(segment.editParams || {}),
                                displayHidden: true,
                            },
                        } as ISegment)
                        trackEvent(gaCategory, 'Delete Cut', captureId)
                        sendMessage('Clip Deleted!')
                    }}
                    onRevertClick={(index) => {
                        const segment = segments[index]
                        saveUserEdit({
                            ...segment,
                            editParams: undefined,
                        } as ISegment)
                        sendMessage('Clip Reverted!')
                    }}
                    onMergeLeft={(index) => {
                        const segment = getMergedSegment([segments[index], segments[index - 1]])
                        if (segment) {
                            saveUserEdit(segment)
                            refreshSegmentsData()
                            trackEvent(gaCategory, 'Merge Left', captureId)
                            sendMessage('Merge Successful!')
                        }
                    }}
                    onMergeRight={(index) => {
                        const segment = getMergedSegment([segments[index], segments[index + 1]])
                        if (segment) {
                            saveUserEdit(segment)
                            refreshSegmentsData()
                            trackEvent(gaCategory, 'Merge Right', captureId)
                            sendMessage('Merge Successful!')
                        }
                    }}
                />
                {selected.length > 0 && <UploadButtons uploadCallback={uploadClicked} />}
                <SupportCTA />
                <BottomBar>
                    <Footer />
                </BottomBar>
                {isProcessingUpload && (
                    <LoaderContainer>
                        <DotsLoader isAbsolute />
                    </LoaderContainer>
                )}
            </div>
        )
    }
}

export default Detail
